/**
 * Settings: the data she owns and edits. Her business name, the catalog (pots with the asset
 * register, clients, spots, requirements), the rules, and who's using this phone. Pure: every change
 * returns new state; the store keeps it on the phone.
 */
import { calendarDate, type CalendarDate, type CatalogData } from "@hitl/rotations-domain";

export interface Rules {
  /** The promise: no repeat at a client within this many months. */
  readonly windowMonths: number;
  /** First day her records are complete (earlier = amber "can't confirm"). */
  readonly historyKnownSince: CalendarDate;
  readonly defaultSpares: number;
}

export interface People {
  readonly names: readonly string[];
  /** Stamped on every record made on this phone. */
  readonly current: string;
}

export interface SettingsState {
  readonly business: string;
  readonly catalog: CatalogData;
  readonly rules: Rules;
  readonly people: People;
}

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, Math.round(n)));

export function setRules(s: SettingsState, rules: Partial<Rules>): SettingsState {
  const next = { ...s.rules, ...rules };
  return {
    ...s,
    rules: {
      windowMonths: clamp(next.windowMonths, 1, 36),
      historyKnownSince: calendarDate(next.historyKnownSince),
      defaultSpares: clamp(next.defaultSpares, 0, 10),
    },
  };
}

/** Add a person (if new) and make them the one using this phone. */
export function usePerson(s: SettingsState, name: string): SettingsState {
  const clean = name.trim().replace(/\s+/g, " ");
  if (!clean) return s;
  const known = s.people.names.find((n) => n.toLowerCase() === clean.toLowerCase());
  return { ...s, people: { names: known ? s.people.names : [...s.people.names, clean], current: known ?? clean } };
}

export function setCatalog(s: SettingsState, catalog: CatalogData): SettingsState {
  return { ...s, catalog };
}

export function setBusiness(s: SettingsState, name: string): SettingsState {
  const clean = name.trim();
  return clean ? { ...s, business: clean } : s;
}
